import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SortingState } from "@tanstack/react-table";
import { CredentialRequestListTemplate } from "../templates";
import { useUserProfile, useCredentialRequestList, useRequestListActions } from "../hooks";
import { getAllDistricts, getRegionsByDistrict } from "@/Services/regions.service";
import type { District } from "@/models/district";
import type { Region } from "@/models/region";
import type { CredentialRequest } from "../types";
import { CREDENTIAL_TYPES } from "@/models/credentialRequest";

/**
 * Page: Credential Request List
 * Orchestrates hooks and passes data to template
 * Filters are persisted in URL query parameters
 */
export function CredentialRequestListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // User profile
  const { userProfile, isAdmin } = useUserProfile();

  // Initialize state from URL params
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page") || "1", 10));
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get("pageSize") || "20", 10));

  // Search state
  const [searchValue, setSearchValue] = useState(searchParams.get("search") || "");

  // Sorting state - default to code descending
  const [sorting, setSorting] = useState<SortingState>([{ id: "code", desc: true }]);

  // Filter state from URL
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
  const [districtFilter, setDistrictFilter] = useState(searchParams.get("districtId") || "");
  const [regionFilter, setRegionFilter] = useState(searchParams.get("regionId") || "");
  const [dateFromFilter, setDateFromFilter] = useState(searchParams.get("dateFrom") || "");
  const [dateToFilter, setDateToFilter] = useState(searchParams.get("dateTo") || "");

  // Filter options
  const [districts, setDistricts] = useState<District[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);

  // Load requests with filters
  const { requests, isLoading, totalPages, totalItems, reloadRequests } =
    useCredentialRequestList({
      statusFilter,
      districtFilter,
      regionFilter,
      dateFromFilter,
      dateToFilter,
      searchValue,
      sorting,
      currentPage,
      pageSize,
      credentialType: CREDENTIAL_TYPES.MINISTERIAL, // ✅ Only show MINISTERIAL requests
    });

  // Request actions
  const { isSubmitting, handleApprove, handleReject, handleMarkPrinted, handleMarkDelivered } =
    useRequestListActions({
      userId: userProfile?.id,
      onSuccess: reloadRequests,
    });

  // Load districts on mount
  useEffect(() => {
    const loadDistricts = async () => {
      const data = await getAllDistricts();
      setDistricts(data);
    };
    loadDistricts();
  }, []);

  // Load regions when district filter changes
  useEffect(() => {
    const loadRegions = async () => {
      if (districtFilter) {
        const data = await getRegionsByDistrict(districtFilter);
        setRegions(data);
        setRegionFilter(""); // Reset region when district changes
      } else {
        setRegions([]);
        setRegionFilter("");
      }
    };
    loadRegions();
  }, [districtFilter]);

  // Sync filters to URL when they change
  const updateURLParams = (updates: Record<string, string>) => {
    const params = new URLSearchParams();
    
    // Preserve existing filters
    const current = {
      page: currentPage.toString(),
      pageSize: pageSize.toString(),
      search: searchValue,
      status: statusFilter,
      districtId: districtFilter,
      regionId: regionFilter,
      dateFrom: dateFromFilter,
      dateTo: dateToFilter,
      ...updates, // Apply updates
    };
    
    // Only add non-empty values to URL
    Object.entries(current).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    
    setSearchParams(params);
  };

  // Wrap filter setters to update URL
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
    updateURLParams({ status: value, page: "1" });
  };

  const handleDistrictFilterChange = (value: string) => {
    setDistrictFilter(value);
    setRegionFilter(""); // Reset region
    setCurrentPage(1);
    updateURLParams({ districtId: value, regionId: "", page: "1" });
  };

  const handleRegionFilterChange = (value: string) => {
    setRegionFilter(value);
    setCurrentPage(1);
    updateURLParams({ regionId: value, page: "1" });
  };

  const handleDateFromChange = (value: string) => {
    setDateFromFilter(value);
    setCurrentPage(1);
    updateURLParams({ dateFrom: value, page: "1" });
  };

  const handleDateToChange = (value: string) => {
    setDateToFilter(value);
    setCurrentPage(1);
    updateURLParams({ dateTo: value, page: "1" });
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
    updateURLParams({ search: value, page: "1" });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    updateURLParams({ page: page.toString() });
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    updateURLParams({ pageSize: size.toString(), page: "1" });
  };

  // Handle row click
  const handleRowClick = (request: CredentialRequest) => {
    navigate(`/credential-requests/${request.id}`);
  };

  // Handle admin actions
  const handleApproveClick = (request: CredentialRequest) => {
    handleApprove(request.id);
  };

  const handleRejectConfirm = (requestId: string, notes: string) => {
    handleReject(requestId, notes);
  };

  const handleMarkPrintedClick = (request: CredentialRequest) => {
    handleMarkPrinted(request.id);
  };

  const handleMarkDeliveredClick = (request: CredentialRequest) => {
    handleMarkDelivered(request.id);
  };

  // Handle create new - navigate directly to new page
  const handleCreateNew = () => {
    navigate('/credential-requests/new');
  };

  // Load modal regions when a district is selected in the new request dialog
  // This is managed by the template's internal state, but we need to provide
  // the regions based on the selected district. Since the template manages
  // the selectedDistrictId internally, we'll need to handle this differently.
  // For now, let's pass districts and handle region loading in the template.

  return (
    <CredentialRequestListTemplate
      requests={requests}
      districts={districts}
      regions={regions}
      isLoading={isLoading}
      isAdmin={isAdmin}
      isSubmitting={isSubmitting}
      currentPage={currentPage}
      totalPages={totalPages}
      totalItems={totalItems}
      pageSize={pageSize}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
      searchValue={searchValue}
      onSearchChange={handleSearchChange}
      sorting={sorting}
      onSortingChange={setSorting}
      statusFilter={statusFilter}
      onStatusFilterChange={handleStatusFilterChange}
      districtFilter={districtFilter}
      onDistrictFilterChange={handleDistrictFilterChange}
      regionFilter={regionFilter}
      onRegionFilterChange={handleRegionFilterChange}
      dateFromFilter={dateFromFilter}
      onDateFromFilterChange={handleDateFromChange}
      dateToFilter={dateToFilter}
      onDateToFilterChange={handleDateToChange}
      onRowClick={handleRowClick}
      onApprove={handleApproveClick}
      onReject={handleRejectConfirm}
      onMarkPrinted={handleMarkPrintedClick}
      onMarkDelivered={handleMarkDeliveredClick}
      onCreateNew={handleCreateNew}
    />
  );
}
